import { useState, useCallback } from 'react';
import { SystemRole } from '@peles/shared';
import { useCreateUser } from '../../hooks/useUsers';
import { GoogleAddressAutocomplete } from '../common/GoogleAddressAutocomplete';
import { PhoneInput } from '../common/PhoneInput';

interface CreateUserPanelProps {
  onClose: () => void;
  onCreated: () => void;
}

interface FormState {
  email: string;
  name: string;
  systemRole: SystemRole;
  phone: string;
  address: string;
}

const initialForm: FormState = {
  email: '',
  name: '',
  systemRole: SystemRole.CLIENT,
  phone: '',
  address: '',
};

export function CreateUserPanel({ onClose, onCreated }: CreateUserPanelProps) {
  const createUser = useCreateUser();
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState('');

  // No email is fine for somebody reached by phone — they cannot sign in, so need a phone.
  const isValid = form.name.trim() !== '' && (form.email.trim() !== '' || form.phone.trim() !== '');
  const noEmail = form.email.trim() === '';

  const handleCreate = useCallback(() => {
    setError('');
    createUser.mutate(
      {
        email: form.email.trim() || null,
        name: form.name.trim(),
        systemRole: form.systemRole,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
      },
      {
        onSuccess: () => {
          onCreated();
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to create user';
          setError(message);
        },
      },
    );
  }, [form, createUser, onCreated]);

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Create User</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="create-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            id="create-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="user@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            {noEmail
              ? 'Without an email they cannot sign in — enter a phone to reach them.'
              : 'They sign in with Google on this email.'}
          </p>
        </div>

        <div>
          <label htmlFor="create-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            id="create-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Full name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="create-role" className="block text-sm font-medium text-gray-700 mb-1">
            Role
          </label>
          <select
            id="create-role"
            value={form.systemRole}
            onChange={(e) => setForm((f) => ({ ...f, systemRole: e.target.value as SystemRole }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {Object.values(SystemRole).map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="create-phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <PhoneInput
            id="create-phone"
            value={form.phone}
            onChange={(phone) => setForm((f) => ({ ...f, phone }))}
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="create-address" className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <GoogleAddressAutocomplete
            id="create-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onAddressSelect={(fields) =>
              setForm((f) => ({ ...f, address: fields.formattedAddress }))
            }
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleCreate}
          disabled={!isValid || createUser.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createUser.isPending ? 'Creating...' : 'Create User'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
